import { useEffect, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { fmtUSD } from "../../lib/analytics";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import type { TeamRole } from "../../lib/types";
import { BILL_CHIP_CLASS, BOOKS_PAYOUT_BILL_SELECT, billChipText, billChipTone, normalizeBill, type BooksPayoutBill } from "../../lib/booksPayoutBills";

/**
 * TL $60k Monthly Override Bonus (Jun 17, 2026).
 *
 * When a rep who rolls up to a team lead exceeds the monthly commissionable-
 * revenue threshold (build-completed basis, default $60k), the TL override on
 * that rep's monthly production effectively goes 1% -> 2%. The extra 1% is paid
 * as a discrete monthly bonus generated at month-end close-out by the
 * `tl_monthly_bonus_close_out` cron (fn `accrue_tl_monthly_bonuses`), which fires
 * on the 1st of each month for the prior month.
 *
 * Surfaces on the Commission Board so Chad approves it alongside everything else;
 * Shannon marks it paid.
 *
 *   Pending tab   -> status='pending'  -> Chad approves
 *   Approved tab  -> status='approved' -> Shannon marks paid
 *   Paid tab      -> status='paid'     -> read-only history
 *
 * Oct 8, 2026 — approving goes through `tl_bonuses_approve`: the row is
 * flagged approved and ONE Books bill per team lead is made for the batch
 * (`books_bill_id`); Shannon pays it in Books and the pull marks the row paid
 * ('books:<method>'). Rows with a bill show the chip and no Mark paid; rows
 * approved before Oct 8 (no bill) keep Shannon's Mark paid here.
 */

type BonusStatus = "pending" | "approved" | "paid" | "voided";

interface BonusRow {
  id: string;
  tl_id: string;
  rep_id: string;
  period_month: string; // YYYY-MM-DD (first of month)
  monthly_commissionable: number | string;
  deals_count: number;
  bonus_rate: number | string;
  threshold: number | string;
  bonus_amount: number | string;
  status: BonusStatus;
  approved_at: string | null;
  paid_at: string | null;
  tl: { full_name: string } | { full_name: string }[] | null;
  rep: { full_name: string } | { full_name: string }[] | null;
  /** Oct 8, 2026 — the Books bill that carries this month, when approved through the RPC. */
  books_bill_id?: string | null;
  bill?: BooksPayoutBill | { [k: string]: unknown }[] | { [k: string]: unknown } | null;
}

function billOf(r: BonusRow): BooksPayoutBill | null {
  const b = Array.isArray(r.bill) ? r.bill[0] : r.bill;
  return b ? normalizeBill(b as Record<string, unknown>) : null;
}

function canApprove(role: TeamRole | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "sales_director" || role === "admin" || role === "director_of_operations";
}
function canMarkPaid(role: TeamRole | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "finance" || role === "admin";
}

function num(v: number | string | null | undefined): number {
  return typeof v === "number" ? v : Number(v) || 0;
}
function name(p: { full_name: string } | { full_name: string }[] | null, fallback: string): string {
  const obj = Array.isArray(p) ? p[0] : p;
  return obj?.full_name ?? fallback;
}
// Date-only -> "May 2026" without the UTC-midnight off-by-one trap.
function fmtMonth(iso: string): string {
  return new Date(iso + "T12:00:00").toLocaleDateString(undefined, { month: "long", year: "numeric" });
}
function fmtDate(iso: string): string {
  return new Date(iso + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const STATUS_FOR_TAB: Record<"pending" | "approved" | "paid", BonusStatus> = {
  pending: "pending",
  approved: "approved",
  paid: "paid",
};

export default function TLMonthlyBonusSection({
  tab,
  role,
  isSuperUser,
  onChanged,
}: {
  tab: "pending" | "approved" | "paid";
  role: TeamRole | undefined;
  isSuperUser: boolean;
  onChanged: () => void;
}) {
  const { member } = useAuth();
  const status = STATUS_FOR_TAB[tab];
  const [rows, setRows] = useState<BonusRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [acting, setActing] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from("tl_monthly_bonuses")
      .select(
        `*, tl:team_roster!tl_monthly_bonuses_tl_id_fkey(full_name), rep:team_roster!tl_monthly_bonuses_rep_id_fkey(full_name), bill:books_payout_bills(${BOOKS_PAYOUT_BILL_SELECT})`,
      )
      .eq("status", status)
      .order("period_month", { ascending: false });
    if (error) { setErr(formatSupabaseError(error)); return; }
    setErr(null);
    setRows((data ?? []) as BonusRow[]);
  }, [status]);

  useEffect(() => { load(); }, [load]);
  useRealtimeTable("tl_monthly_bonuses", load);

  async function approve(id: string) {
    setActing((p) => new Set(p).add(id));
    // Oct 8, 2026 — the RPC flags the row and makes the team lead's Books bill.
    const { error } = await supabase.rpc("tl_bonuses_approve", { p_ids: [id] });
    setActing((p) => { const n = new Set(p); n.delete(id); return n; });
    if (error) { setErr(formatSupabaseError(error)); return; }
    onChanged();
    load();
  }

  async function markPaid(id: string) {
    setActing((p) => new Set(p).add(id));
    const { error } = await supabase
      .from("tl_monthly_bonuses")
      .update({ status: "paid", paid_by: member?.id ?? null, paid_at: new Date().toISOString() })
      .eq("id", id);
    setActing((p) => { const n = new Set(p); n.delete(id); return n; });
    if (error) { setErr(formatSupabaseError(error)); return; }
    onChanged();
    load();
  }

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-bardin-ink">⭐ TL $60k Bonus</h2>
          <p className="text-xs text-slate-500">
            Extra 1% to the team lead when a rep clears ${num(rows?.[0]?.threshold ?? 60000).toLocaleString()}+ commissionable in a month (build-completed).{" "}
            {tab === "pending" ? "Approve to release." : tab === "approved" ? "Mark paid." : "History."}
          </p>
        </div>
        {rows && rows.length > 0 && (
          <div className="text-right">
            <div className="text-sm font-semibold text-bardin-ink">
              {fmtUSD(rows.reduce((s, r) => s + num(r.bonus_amount), 0))}
            </div>
            <div className="text-xs text-slate-500">{rows.length} {rows.length === 1 ? "bonus" : "bonuses"}</div>
          </div>
        )}
      </div>

      {err && (
        <div className="mb-2 text-xs bg-red-50 text-red-700 rounded px-3 py-2">{err}</div>
      )}

      {rows === null ? (
        <div className="bg-white rounded-xl shadow p-4 text-sm text-slate-400">Loading TL bonuses…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
          No TL monthly bonuses {tab === "pending" ? "awaiting approval" : tab}.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const ratePct = (num(r.bonus_rate) * 100).toFixed(0);
            return (
              <div key={r.id} className="bg-white rounded-xl shadow p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{name(r.tl, "Team Lead")}</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-medium capitalize">{r.status}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {fmtMonth(r.period_month)} · driven by {name(r.rep, "rep")}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {fmtUSD(num(r.monthly_commissionable))} commissionable across {r.deals_count} {r.deals_count === 1 ? "build" : "builds"} · +{ratePct}% override bonus
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-bardin-ink">{fmtUSD(num(r.bonus_amount))}</div>
                    <div className="text-xs text-slate-500">bonus</div>
                  </div>
                </div>

                {tab === "pending" && canApprove(role, isSuperUser) && (
                  <div className="mt-3">
                    <button
                      onClick={() => approve(r.id)}
                      disabled={acting.has(r.id)}
                      className="px-4 py-1.5 text-xs font-medium rounded text-white bg-bardin-orange hover:opacity-90 disabled:opacity-50"
                    >
                      {acting.has(r.id) ? "Approving…" : "Approve bonus"}
                    </button>
                  </div>
                )}
                {tab === "approved" && r.books_bill_id && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span
                      title={billOf(r) ? billChipText(billOf(r)!) : "Bill in Books"}
                      className={"inline-block text-[11px] font-semibold px-2 py-1 rounded " + BILL_CHIP_CLASS[billOf(r) ? billChipTone(billOf(r)!) : "sent"]}
                    >
                      {billOf(r) ? billChipText(billOf(r)!) : "Bill in Books"}
                    </span>
                    <span className="text-[11px] text-slate-400 italic">Paid in Books; the row reads paid once Books reports back.</span>
                  </div>
                )}
                {tab === "approved" && !r.books_bill_id && canMarkPaid(role, isSuperUser) && (
                  <div className="mt-3">
                    <button
                      onClick={() => markPaid(r.id)}
                      disabled={acting.has(r.id)}
                      className="px-4 py-1.5 text-xs font-medium rounded text-white bg-emerald-600 hover:opacity-90 disabled:opacity-50"
                    >
                      {acting.has(r.id) ? "Marking…" : "Mark paid"}
                    </button>
                  </div>
                )}
                {tab === "paid" && r.paid_at && (
                  <div className="mt-2 text-xs text-slate-400">Paid {fmtDate(r.paid_at.slice(0, 10))}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
